import { useEffect, useState } from 'react'
import { Save, RotateCcw } from 'lucide-react'
import { toast } from 'sonner'

import { settings as settingsApi } from '../api/endpoints'
import { Button } from '../components/Button'
import { Card, CardContent } from '../components/Card'
import { FormField } from '../components/FormField'
import { Input } from '../components/Input'
import { PageLoader } from '../components/Spinner'

function parsePaise(v: string): number { return Number(v) || 0 }

const GROUPS = [
  { key: 'pricing', label: 'Pricing' },
  { key: 'affiliate', label: 'Affiliate' },
  { key: 'stats', label: 'Stats' },
  { key: 'general', label: 'General' },
]

export function SettingsPage() {
  const [settings, setSettings] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [group, setGroup] = useState('pricing')

  function reload() {
    setLoading(true)
    // Stats settings live under the 'general' prefix but get their own tab
    const fetchGroup = group === 'stats' ? 'general' : group
    settingsApi
      .get(fetchGroup)
      .then((r) => setSettings(r.settings))
      .catch((err) => toast.error('Failed to load settings', { description: err.message }))
      .finally(() => setLoading(false))
  }

  useEffect(reload, [group])

  function set(key: string, value: string) {
    setSettings((prev) => ({ ...prev, [key]: value }))
  }

  async function handleSave() {
    setSaving(true)
    try {
      const result = await settingsApi.update(settings)
      setSettings((prev) => ({ ...prev, ...result.settings }))
      toast.success('Settings saved')
    } catch (err) {
      toast.error('Failed to save', { description: (err as Error).message })
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <PageLoader />

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-ink">Settings</h1>
          <p className="text-sm text-slatey mt-1">Configure site-wide defaults</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={reload}><RotateCcw className="size-4" /></Button>
          <Button size="sm" onClick={handleSave} disabled={saving}><Save className="size-4" />{saving ? 'Saving…' : 'Save'}</Button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-line pb-2">
        {GROUPS.map((g) => (
          <button
            key={g.key}
            onClick={() => setGroup(g.key)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              group === g.key ? 'bg-forest text-white' : 'text-slatey hover:text-ink hover:bg-ivory'
            }`}
          >
            {g.label}
          </button>
        ))}
      </div>

      <Card>
        <CardContent className="space-y-5">
          {group === 'pricing' && (
            <>
              <FormField label="Free Shipping Threshold (₹)" hint="Orders at or above this get free shipping">
                <Input type="number" value={parsePaise(settings['pricing.freeShippingThreshold']) / 100}
                  onChange={(e) => set('pricing.freeShippingThreshold', String(Math.round(Number(e.target.value) * 100)))} />
              </FormField>
              <FormField label="Flat Shipping Fee (₹)" hint="Charged when below threshold — within same state">
                <Input type="number" value={parsePaise(settings['pricing.flatShippingFee']) / 100}
                  onChange={(e) => set('pricing.flatShippingFee', String(Math.round(Number(e.target.value) * 100)))} />
              </FormField>
              <FormField label="Out-of-State Shipping Fee (₹)" hint="Charged when below threshold — different state">
                <Input type="number" value={parsePaise(settings['pricing.outOfStateShippingFee']) / 100}
                  onChange={(e) => set('pricing.outOfStateShippingFee', String(Math.round(Number(e.target.value) * 100)))} />
              </FormField>
              <FormField label="GST Rate (%)">
                <Input type="number" value={settings['pricing.gstRate'] || '5'}
                  onChange={(e) => set('pricing.gstRate', e.target.value)} />
              </FormField>
              <FormField label="COD Fee (₹)">
                <Input type="number" value={parsePaise(settings['pricing.codFee']) / 100}
                  onChange={(e) => set('pricing.codFee', String(Math.round(Number(e.target.value) * 100)))} />
              </FormField>
              <FormField label="COD Enabled">
                <select className="w-full rounded-md border border-line bg-white px-3 py-2 text-sm"
                  value={settings['pricing.codEnabled'] || 'true'}
                  onChange={(e) => set('pricing.codEnabled', e.target.value)}>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </FormField>
            </>
          )}

          {group === 'affiliate' && (
            <>
              <FormField label="Default Commission Rate (%)" hint="Applied when no product or affiliate override exists">
                <Input type="number" value={settings['affiliate.commissionRate'] || '10'}
                  onChange={(e) => set('affiliate.commissionRate', e.target.value)} />
              </FormField>
              <FormField label="Referral Cookie Duration (days)">
                <Input type="number" value={settings['affiliate.cookieDays'] || '30'}
                  onChange={(e) => set('affiliate.cookieDays', e.target.value)} />
              </FormField>
              <FormField label="Minimum Payout (₹)">
                <Input type="number" value={parsePaise(settings['affiliate.minPayout']) / 100}
                  onChange={(e) => set('affiliate.minPayout', String(Math.round(Number(e.target.value) * 100)))} />
              </FormField>
            </>
          )}

          {group === 'stats' && <StatsEditor settings={settings} onChange={set} />}

          {group === 'general' && (
            <>
              <FormField label="Site Name">
                <Input value={settings['general.siteName'] || 'Dr.Herbal'} onChange={(e) => set('general.siteName', e.target.value)} />
              </FormField>
              <FormField label="Support Phone">
                <Input value={settings['general.supportPhone'] || ''} onChange={(e) => set('general.supportPhone', e.target.value)} />
              </FormField>
              <FormField label="Support Email">
                <Input value={settings['general.supportEmail'] || ''} onChange={(e) => set('general.supportEmail', e.target.value)} />
              </FormField>
              <FormField label="Return Window (days)">
                <Input type="number" value={settings['general.returnWindowDays'] || '14'}
                  onChange={(e) => set('general.returnWindowDays', e.target.value)} />
              </FormField>
              <FormField label="Store State" hint="Used to determine same-state vs out-of-state shipping">
                <Input value={settings['general.storeState'] || 'West Bengal'} onChange={(e) => set('general.storeState', e.target.value)} />
              </FormField>
              <FormField label="Maintenance Mode">
                <select className="w-full rounded-md border border-line bg-white px-3 py-2 text-sm"
                  value={settings['general.maintenanceMode'] || 'false'}
                  onChange={(e) => set('general.maintenanceMode', e.target.value)}>
                  <option value="false">Off</option>
                  <option value="true">On</option>
                </select>
              </FormField>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

/** Editable stats & promises section. Stats are stored as JSON arrays
 *  in settings keys 'general.stats' and 'general.promises'. Fallback
 *  to sensible defaults if the settings are empty/missing. */
function StatsEditor({
  settings,
  onChange,
}: {
  settings: Record<string, string>
  onChange: (key: string, value: string) => void
}) {
  const DEFAULT_STATS = [
    { value: '25L+', label: 'Happy Customers', description: 'Loved across India' },
    { value: '100%', label: 'Ayurvedic & Herbal', description: 'Pure ingredients only' },
    { value: '30,000+', label: 'Verified Reviews', description: 'Real customer feedback' },
    { value: '50+', label: 'Expert Formulations', description: 'Crafted by vaidyas' },
  ]

  const DEFAULT_PROMISES = [
    { icon: 'Leaf', title: 'Pure & Natural', description: 'No toxins, no shortcuts, no harsh chemicals—just care that respects your Vata, Pitta, Kapha.' },
    { icon: 'FlaskConical', title: 'Science + Ayurveda', description: 'Formulated using herbs backed by scientific studies and time-tested Ayurvedic principles.' },
    { icon: 'ShieldCheck', title: 'Trusted by Experts', description: 'Created by qualified modern vaidyas, ayurvedic experts and refined for today\'s lifestyle.' },
    { icon: 'Heart', title: 'Cruelty-Free', description: 'Clean, conscious, and cruelty-free formulations that prioritize your wellbeing.' },
  ]

  function parseStats(): Array<{ value: string; label: string; description: string }> {
    try {
      const raw = settings['general.stats']
      if (raw) return JSON.parse(raw)
    } catch {}
    return DEFAULT_STATS
  }

  function parsePromises(): Array<{ icon: string; title: string; description: string }> {
    try {
      const raw = settings['general.promises']
      if (raw) return JSON.parse(raw)
    } catch {}
    return DEFAULT_PROMISES
  }

  const stats = parseStats()
  const promises = parsePromises()

  const updateStats = (idx: number, field: string, val: string) => {
    const next = stats.map((s, i) => (i === idx ? { ...s, [field]: val } : s))
    onChange('general.stats', JSON.stringify(next))
  }

  const updatePromise = (idx: number, field: string, val: string) => {
    const next = promises.map((p, i) => (i === idx ? { ...p, [field]: val } : p))
    onChange('general.promises', JSON.stringify(next))
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-semibold text-ink mb-3">Homepage Stats</h3>
        <div className="space-y-3">
          {stats.map((stat, i) => (
            <div key={i} className="grid grid-cols-3 gap-2">
              <Input
                placeholder="Value"
                value={stat.value}
                onChange={(e) => updateStats(i, 'value', e.target.value)}
              />
              <Input
                placeholder="Label"
                value={stat.label}
                onChange={(e) => updateStats(i, 'label', e.target.value)}
              />
              <Input
                placeholder="Description"
                value={stat.description}
                onChange={(e) => updateStats(i, 'description', e.target.value)}
              />
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-ink mb-3">Philosophy Promises</h3>
        <div className="space-y-3">
          {promises.map((p, i) => (
            <div key={i} className="space-y-2 border border-line rounded-lg p-3">
              <div className="grid grid-cols-2 gap-2">
                <FormField label="Icon (Lucide name)" className="mb-0">
                  <Input
                    value={p.icon}
                    onChange={(e) => updatePromise(i, 'icon', e.target.value)}
                  />
                </FormField>
                <FormField label="Title" className="mb-0">
                  <Input
                    value={p.title}
                    onChange={(e) => updatePromise(i, 'title', e.target.value)}
                  />
                </FormField>
              </div>
              <FormField label="Description" className="mb-0">
                <Input
                  value={p.description}
                  onChange={(e) => updatePromise(i, 'description', e.target.value)}
                />
              </FormField>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
